import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import SimpleTooltip from '../SimpleTooltip';
import { Play, Pause, SkipForward, SkipBack, X, Maximize2, Minimize2, ChevronRight, Lightbulb, Monitor, Rocket, Shield, PieChart, Upload, Users, BarChart3, Settings, Share2, Headphones, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SECTION_DURATION = 8000;

function getSectionIcon(title) {
  const t = title.toLowerCase();
  if (t.includes('problem')) return Lightbulb;
  if (t.includes('approach') || t.includes('concept')) return Shield;
  if (t.includes('privacy') || t.includes('security')) return Eye;
  if (t.includes('who') || t.includes('sharing') || t.includes('collaboration')) return Users;
  if (t.includes('dashboard') || t.includes('analytics')) return BarChart3;
  if (t.includes('investment') || t.includes('add')) return PieChart;
  if (t.includes('upload') || t.includes('csv') || t.includes('bulk')) return Upload;
  if (t.includes('support') || t.includes('getting')) return Headphones;
  if (t.includes('next') || t.includes('started') || t.includes('explore')) return Rocket;
  if (t.includes('setting') || t.includes('customize')) return Settings;
  if (t.includes('overview') || t.includes('tour')) return Monitor;
  if (t.includes('share')) return Share2;
  return ChevronRight;
}

const getDotScale = (i, currentSection) => {
  if (i < currentSection) return 'scale-100';
  if (i === currentSection) return 'scale-125';
  return 'scale-75 opacity-40';
};

export function WalkthroughPlayer({ video, onClose, onProgress }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [sectionProgress, setSectionProgress] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const progressRef = useRef(null);
  const sections = video.sections || [];

  const goToSection = useCallback((idx) => {
    if (idx >= 0 && idx < sections.length) {
      setCurrentSection(idx); setSectionProgress(0);
      onProgress?.(video.video_id, idx, idx === sections.length - 1);
    }
  }, [sections.length, video.video_id, onProgress]);

  useEffect(() => {
    if (!playing) return;
    const interval = 50;
    let elapsed = 0;
    progressRef.current = setInterval(() => {
      elapsed += interval;
      setSectionProgress(Math.min(100, (elapsed / SECTION_DURATION) * 100));
      if (elapsed >= SECTION_DURATION) {
        if (currentSection < sections.length - 1) {
          setCurrentSection(prev => prev + 1); setSectionProgress(0); elapsed = 0;
          onProgress?.(video.video_id, currentSection + 1, currentSection + 1 === sections.length - 1);
        } else {
          setPlaying(false); setSectionProgress(100);
          onProgress?.(video.video_id, currentSection, true);
        }
      }
    }, interval);
    return () => clearInterval(progressRef.current);
  }, [playing, currentSection, sections.length, onProgress, video.video_id]);

  useEffect(() => { setSectionProgress(0); }, [currentSection]);

  const totalProgress = sections.length > 0 ? ((currentSection + sectionProgress / 100) / sections.length) * 100 : 0;
  const section = sections[currentSection] || {};
  const SectionIcon = getSectionIcon(section.title || '');
  const accentColor = video.color || '#10B981';

  const playerContent = (
    <div className="flex flex-col h-full bg-slate-950 text-white rounded-lg overflow-hidden" data-testid={`player-${video.video_id}`}>
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-900 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: accentColor }} />
          <span className="text-sm font-semibold truncate">{video.title}</span>
          {sections.length > 0 && (
            <Badge className="text-[10px] bg-slate-800 text-slate-300">{currentSection + 1}/{sections.length}</Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <SimpleTooltip content={expanded ? 'Restore player size' : 'Expand player'}>
            <Button variant="ghost" size="icon" aria-label={expanded ? 'Restore player size' : 'Expand player'} className="h-7 w-7 text-slate-400 hover:text-white" onClick={() => setExpanded(!expanded)}>
              {expanded ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </Button>
          </SimpleTooltip>
          <SimpleTooltip content="Close walkthrough">
            <Button variant="ghost" size="icon" aria-label="Close walkthrough" className="h-7 w-7 text-slate-400 hover:text-white" onClick={onClose}><X className="w-3.5 h-3.5" /></Button>
          </SimpleTooltip>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 relative overflow-hidden" style={{ minHeight: expanded ? 400 : 280 }}>
        {(video.video_url_webm || video.video_url_mp4 || video.video_url) ? (
          <video
            key={`${video.video_url_webm || ''}|${video.video_url_mp4 || ''}|${video.video_url || ''}`}
            className="w-full h-full object-contain rounded"
            controls
            autoPlay={playing}
            data-testid="video-element"
          >
            {video.video_url_webm && (
              <source src={`${BACKEND_URL}${video.video_url_webm}`} type="video/webm" />
            )}
            {video.video_url_mp4 && (
              <source src={`${BACKEND_URL}${video.video_url_mp4}`} type="video/mp4" />
            )}
            {/* Legacy single-source fallback for documents created before dual-source support */}
            {video.video_url && !video.video_url_webm && !video.video_url_mp4 && (
              <source src={`${BACKEND_URL}${video.video_url}`} />
            )}
          </video>
        ) : (
          <>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full blur-3xl" style={{ background: accentColor, animation: 'pulse 4s infinite' }} />
              <div className="absolute bottom-1/4 right-1/4 w-24 h-24 rounded-full blur-3xl" style={{ background: accentColor, animation: 'pulse 4s infinite 2s' }} />
            </div>
            {sections.length > 0 ? (
              <div key={currentSection} className="relative z-10 text-center max-w-md animate-in fade-in slide-in-from-bottom-4 duration-700">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg" style={{ background: `${accentColor}20`, border: `2px solid ${accentColor}40` }}>
                  <SectionIcon className="w-8 h-8" style={{ color: accentColor }} />
                </div>
                <h3 className="text-xl font-bold mb-3">{section.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{section.description}</p>
              </div>
            ) : (
              <div className="relative z-10 text-center max-w-sm">
                <p className="text-sm text-slate-400">Video coming soon.</p>
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex items-center justify-center gap-1.5 py-2">
        {sections.map((_, i) => (
          <button key={`step-${i}`} onClick={() => { goToSection(i); setPlaying(true); }} className="group relative" data-testid={`section-dot-${i}`}>
            <div className={`w-2 h-2 rounded-full transition-all duration-300 ${getDotScale(i, currentSection)}`} style={{ background: i <= currentSection ? accentColor : '#475569' }} />
          </button>
        ))}
      </div>
      {sections.length > 0 && (
        <div className="h-1 bg-slate-800"><div className="h-full transition-all duration-100 ease-linear" style={{ width: `${totalProgress}%`, background: accentColor }} /></div>
      )}
      {sections.length > 0 && (
        <div className="flex items-center justify-between px-4 py-2 bg-slate-900 border-t border-slate-800">
          <div className="flex items-center gap-1">
            <SimpleTooltip content="Previous section">
              <Button variant="ghost" size="icon" aria-label="Previous section" className="h-8 w-8 text-slate-400 hover:text-white" disabled={currentSection === 0} onClick={() => { goToSection(currentSection - 1); setPlaying(true); }} data-testid="player-prev"><SkipBack className="w-4 h-4" /></Button>
            </SimpleTooltip>
            <SimpleTooltip content={playing ? 'Pause' : 'Play'}>
              <Button variant="ghost" size="icon" aria-label={playing ? 'Pause' : 'Play'} className="h-9 w-9 text-white hover:text-white" onClick={() => { if (!playing && currentSection === sections.length - 1 && sectionProgress >= 100) goToSection(0); setPlaying(!playing); }} data-testid="player-play-pause">{playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}</Button>
            </SimpleTooltip>
            <SimpleTooltip content="Next section">
              <Button variant="ghost" size="icon" aria-label="Next section" className="h-8 w-8 text-slate-400 hover:text-white" disabled={currentSection >= sections.length - 1} onClick={() => { goToSection(currentSection + 1); setPlaying(true); }} data-testid="player-next"><SkipForward className="w-4 h-4" /></Button>
            </SimpleTooltip>
          </div>
          <span className="text-xs text-slate-500">{section.title}</span>
        </div>
      )}
    </div>
  );

  if (expanded) {
    return (
      <Dialog open onOpenChange={() => setExpanded(false)}>
        <DialogContent className="max-w-3xl p-0 border-0 bg-transparent [&>button]:hidden">
          <DialogTitle className="sr-only">{video.title}</DialogTitle>
          <DialogDescription className="sr-only">Walkthrough player — section {currentSection + 1} of {sections.length}.</DialogDescription>
          {playerContent}
        </DialogContent>
      </Dialog>
    );
  }
  return playerContent;
}
